import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Button, Alert, CircularProgress,
  Grid, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Tooltip, IconButton, LinearProgress, Divider
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Warning, CheckCircle, Error, Analytics,
  Assessment, Timeline, Psychology, Speed, BarChart, PieChart,
  Visibility, Refresh, Download, FilterList
} from '@mui/icons-material';
import {
  LineChart, Line, AreaChart, Area, BarChart as RechartsBarChart, Bar,
  PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

interface AnalyticsSummary {
  total_customers: number;
  high_risk_customers: number;
  medium_risk_customers: number;
  low_risk_customers: number;
  high_severity_anomalies: number;
  medium_severity_anomalies: number;
  urgent_insights: number;
}

interface TrendData {
  date: string;
  value: number;
  change_percent: number;
  trend_direction: string;
}

interface AnomalyData {
  customer_id: number;
  customer_name: string;
  anomaly_type: string;
  severity: string;
  description: string;
  detected_at: string;
  confidence: number;
}

interface HealthScore {
  customer_id: number;
  customer_name: string;
  overall_score: number;
  check_in_score: number;
  response_score: number;
  alert_score: number;
  caregiver_score: number;
  risk_level: string;
  factors: string[];
  last_updated: string;
}

interface PredictiveInsight {
  customer_id: number;
  customer_name: string;
  insight_type: string;
  probability: number;
  description: string;
  recommended_actions: string[];
  generated_at: string;
}

interface BenchmarkData {
  metric: string;
  current_value: number;
  benchmark_value: number;
  percentile: number;
  performance: string;
}

interface RiskAssessment {
  risk_distribution: {
    high: number;
    medium: number;
    low: number;
  };
  high_risk_customers: any[];
  medium_risk_customers: any[];
  low_risk_customers: any[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const AnalyticsDashboard: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Analytics data
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [trends, setTrends] = useState<any>(null);
  const [anomalies, setAnomalies] = useState<AnomalyData[]>([]);
  const [healthScores, setHealthScores] = useState<HealthScore[]>([]);
  const [insights, setInsights] = useState<PredictiveInsight[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkData[]>([]);
  const [riskAssessment, setRiskAssessment] = useState<RiskAssessment | null>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch comprehensive dashboard data
      const response = await fetch('/api/v1/analytics/dashboard', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data.summary);
        setTrends(data.trends);
        setAnomalies(data.anomalies);
        setHealthScores(data.health_scores);
        setInsights(data.predictive_insights);
        setBenchmarks(data.benchmarks);
      } else {
        setError('Failed to fetch analytics data');
      }
      
      // Fetch risk assessment
      const riskResponse = await fetch('/api/v1/analytics/risk-assessment', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (riskResponse.ok) {
        const riskData = await riskResponse.json();
        setRiskAssessment(riskData);
      }
      
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case 'excellent': return 'success';
      case 'good': return 'primary';
      case 'average': return 'warning';
      case 'poor': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <Analytics sx={{ mr: 2, verticalAlign: 'middle' }} />
          Advanced Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Intelligent insights and predictive analytics for customer wellness
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Total Customers</Typography>
                <Typography variant="h4">{summary.total_customers}</Typography>
                <Box display="flex" gap={1} sx={{ mt: 1 }}>
                  <Chip label={`${summary.high_risk_customers} High Risk`} color="error" size="small" />
                  <Chip label={`${summary.medium_risk_customers} Medium Risk`} color="warning" size="small" />
                  <Chip label={`${summary.low_risk_customers} Low Risk`} color="success" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Anomalies Detected</Typography>
                <Typography variant="h4">{summary.high_severity_anomalies + summary.medium_severity_anomalies}</Typography>
                <Box display="flex" gap={1} sx={{ mt: 1 }}>
                  <Chip label={`${summary.high_severity_anomalies} High`} color="error" size="small" />
                  <Chip label={`${summary.medium_severity_anomalies} Medium`} color="warning" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Predictive Insights</Typography>
                <Typography variant="h4">{insights.length}</Typography>
                <Box display="flex" gap={1} sx={{ mt: 1 }}>
                  <Chip label={`${summary.urgent_insights} Urgent`} color="error" size="small" />
                  <Chip label={`${insights.length - summary.urgent_insights} Regular`} color="info" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Success Rate</Typography>
                <Typography variant="h4">{trends?.overall_success_rate?.toFixed(1) || 0}%</Typography>
                <Typography variant="body2" color="textSecondary">
                  Avg Response: {trends?.avg_response_time_hours?.toFixed(1) || 0}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Overview" icon={<BarChart />} />
        <Tab label="Trends" icon={<Timeline />} />
        <Tab label="Risk Assessment" icon={<Assessment />} />
        <Tab label="Anomalies" icon={<Warning />} />
        <Tab label="Predictive Insights" icon={<Psychology />} />
        <Tab label="Benchmarks" icon={<Speed />} />
      </Tabs>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Box>
          <Grid container spacing={3}>
            {/* Risk Distribution Chart */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Customer Risk Distribution</Typography>
                  {riskAssessment && (
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPieChart>
                        <Pie
                          data={[
                            { name: 'High Risk', value: riskAssessment.risk_distribution.high, color: '#f44336' },
                            { name: 'Medium Risk', value: riskAssessment.risk_distribution.medium, color: '#ff9800' },
                            { name: 'Low Risk', value: riskAssessment.risk_distribution.low, color: '#4caf50' }
                          ]}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                        >
                          {riskAssessment.risk_distribution.high > 0 && <Cell fill="#f44336" />}
                          {riskAssessment.risk_distribution.medium > 0 && <Cell fill="#ff9800" />}
                          {riskAssessment.risk_distribution.low > 0 && <Cell fill="#4caf50" />}
                        </Pie>
                        <RechartsTooltip />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Success Rate Trend */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Success Rate Trend (30 Days)</Typography>
                  {trends?.success_rate_trends && (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trends.success_rate_trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Anomalies */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Recent Anomalies</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Customer</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Severity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {anomalies.slice(0, 5).map((anomaly) => (
                          <TableRow key={anomaly.customer_id}>
                            <TableCell>{anomaly.customer_name}</TableCell>
                            <TableCell>{anomaly.anomaly_type.replace('_', ' ')}</TableCell>
                            <TableCell>
                              <Chip
                                label={anomaly.severity}
                                color={getSeverityColor(anomaly.severity)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Top Insights */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Top Predictive Insights</Typography>
                  {insights.slice(0, 5).map((insight, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {insight.customer_name} - {insight.insight_type.replace('_', ' ')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {insight.description}
                      </Typography>
                      <Chip
                        label={`${(insight.probability * 100).toFixed(0)}% probability`}
                        color={insight.probability >= 0.8 ? 'error' : 'warning'}
                        size="small"
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Trends Tab */}
      {activeTab === 1 && trends && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Check-in Success Rate Trends</Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={trends.success_rate_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} name="Success Rate (%)" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Response Time Trends</Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={trends.response_time_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" stroke="#82ca9d" strokeWidth={2} name="Avg Response Time (hours)" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Risk Assessment Tab */}
      {activeTab === 2 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Customer Health Scores</Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Customer</TableCell>
                          <TableCell>Overall Score</TableCell>
                          <TableCell>Risk Level</TableCell>
                          <TableCell>Check-in Score</TableCell>
                          <TableCell>Response Score</TableCell>
                          <TableCell>Alert Score</TableCell>
                          <TableCell>Caregiver Score</TableCell>
                          <TableCell>Factors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {healthScores.map((score) => (
                          <TableRow key={score.customer_id}>
                            <TableCell>{score.customer_name}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">{score.overall_score.toFixed(1)}</Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={score.overall_score}
                                  sx={{ width: 60, height: 8 }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={score.risk_level}
                                color={getRiskColor(score.risk_level)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{score.check_in_score.toFixed(1)}</TableCell>
                            <TableCell>{score.response_score.toFixed(1)}</TableCell>
                            <TableCell>{score.alert_score.toFixed(1)}</TableCell>
                            <TableCell>{score.caregiver_score.toFixed(1)}</TableCell>
                            <TableCell>
                              {score.factors.map((factor, index) => (
                                <Chip key={index} label={factor} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Anomalies Tab */}
      {activeTab === 3 && (
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Detected Anomalies</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Anomaly Type</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Detected</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {anomalies.map((anomaly, index) => (
                      <TableRow key={index}>
                        <TableCell>{anomaly.customer_name}</TableCell>
                        <TableCell>{anomaly.anomaly_type.replace('_', ' ')}</TableCell>
                        <TableCell>
                          <Chip
                            label={anomaly.severity}
                            color={getSeverityColor(anomaly.severity)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{anomaly.description}</TableCell>
                        <TableCell>{(anomaly.confidence * 100).toFixed(0)}%</TableCell>
                        <TableCell>{formatDate(anomaly.detected_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Predictive Insights Tab */}
      {activeTab === 4 && (
        <Box>
          <Grid container spacing={3}>
            {insights.map((insight, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6">{insight.customer_name}</Typography>
                      <Chip
                        label={`${(insight.probability * 100).toFixed(0)}% probability`}
                        color={insight.probability >= 0.8 ? 'error' : 'warning'}
                      />
                    </Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {insight.insight_type.replace('_', ' ')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {insight.description}
                    </Typography>
                    <Typography variant="subtitle2" gutterBottom>
                      Recommended Actions:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {insight.recommended_actions.map((action, actionIndex) => (
                        <Typography key={actionIndex} component="li" variant="body2">
                          {action}
                        </Typography>
                      ))}
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                      Generated: {formatDate(insight.generated_at)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Benchmarks Tab */}
      {activeTab === 5 && (
        <Box>
          <Grid container spacing={3}>
            {benchmarks.map((benchmark, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {benchmark.metric.replace('_', ' ').toUpperCase()}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h4">{benchmark.current_value.toFixed(1)}</Typography>
                      <Chip
                        label={benchmark.performance}
                        color={getPerformanceColor(benchmark.performance)}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Benchmark: {benchmark.benchmark_value.toFixed(1)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={benchmark.percentile}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {benchmark.percentile.toFixed(0)}% of benchmark
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default AnalyticsDashboard;
